import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { z } from "zod";
import { DEFAULT_CATEGORY_OPTIONS } from "./config";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../E3StaticData/edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

const filterE3TransactionsSchema = z.object({
  e3_portfolio_client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  e3_portfolio_vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  e3_portfolio_account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  e3_portfolio_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  e3_portfolio_bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  actif_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  e3_static_data_actif_class_contains: z.string().optional(),
  e3_static_data_actif_subclass_contains: z.string().optional(),
  category_contains: z.string().optional(),
  transfer_contains: z.string().optional(),
  fee_type_contains: z.string().optional(),
  description_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  e3_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

export type FilterE3TransactionsSchema = z.infer<
  typeof filterE3TransactionsSchema
>;

const portfolioDepFields: readonly DepField<FilterE3TransactionsSchema>[] = [
  {
    name: "e3_portfolio_bank_name_contains",
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "e3_portfolio_client_contains",
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "e3_portfolio_vehicle_contains",
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "e3_portfolio_name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "e3_portfolio_account_name_contains",
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterE3Transactions() {
  const { t } = useTranslation("tables");
  const tableName = "e3_transactions";

  const fields: Field[] = [
    {
      name: "e3_portfolio_client_contains",
      label: `Client`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_portfolios",
      column_name: "client",
      filters: makeFiltersBuilder<FilterE3TransactionsSchema>(
        portfolioDepFields,
        "e3_portfolio_client_contains",
      ),
    },
    {
      name: "e3_portfolio_vehicle_contains",
      label: `Véhicule / Bénéficiaire`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_portfolios",
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterE3TransactionsSchema>(
        portfolioDepFields,
        "e3_portfolio_vehicle_contains",
      ),
    },
    {
      name: "e3_portfolio_account_name_contains",
      label: `Groupe de compte`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_portfolios",
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterE3TransactionsSchema>(
        portfolioDepFields,
        "e3_portfolio_account_name_contains",
      ),
    },
    {
      name: "e3_portfolio_name_contains",
      label: `Compte`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_portfolios",
      column_name: "name",
      filters: makeFiltersBuilder<FilterE3TransactionsSchema>(
        portfolioDepFields,
        "e3_portfolio_name_contains",
      ),
    },
    {
      name: "e3_portfolio_bank_name_contains",
      label: `Banque`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_portfolios",
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterE3TransactionsSchema>(
        portfolioDepFields,
        "e3_portfolio_bank_name_contains",
      ),
    },
    {
      name: "actif_contains",
      label: `Actif`,
      type: "asyncAutocomplete",
      base_url: "e3/e3_static_data",
      column_name: "name",
    },
    {
      name: "e3_static_data_actif_class_contains",
      label: t(`${tableName}.searchbox.e3_static_data_actif_class_contains`),
      type: "select",
      useTranslation: true,
      client: "e3",
      table_name: "e3_static_data",
      language: "fr",
      translate_key: "asset_class_1",
    },
    {
      name: "e3_static_data_actif_subclass_contains",
      label: t(`${tableName}.searchbox.e3_static_data_actif_subclass_contains`),
      type: "sectionSelect",
      useTranslation: true,
      useTransactionForSectionName: true,
      section_name_translation_key: "asset_class_1",
      section_name_table_name: "e3_static_data",
      client: "e3",
      table_name: "e3_static_data",
      language: "fr",
      translate_key: "asset_class_2",
      options: DEFAULT_ASSET_CLASS_OPTIONS,
    },
    {
      name: "category_contains",
      label: `Type de mouvement`,
      type: "sectionSelect",
      useTranslation: true,
      translate_key: "category",
      client: "e3",
      table_name: "e3_transactions",
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      options: DEFAULT_CATEGORY_OPTIONS,
    },
    {
      name: "transfer_contains",
      label: `Type de transfert`,
      type: "select",
      useTranslation: true,
      client: "e3",
      table_name: "e3_transactions",
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "transfer",
    },
    {
      name: "fee_type_contains",
      label: `Type de frais`,
      type: "select",
      useTranslation: true,
      client: "e3",
      table_name: "e3_transactions",
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "fee_type",
    },
    {
      name: "description_contains",
      label: `Description`,
      type: "input",
    },
    {
      name: "date",
      type: "dateRange",
      label: "Date de valeur",
      from: { name: "date_from" },
      to: { name: "date_to" },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: "Date de création",
      from: { name: "create_from" },
      to: { name: "create_to" },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      columns={2}
      client="e3"
      schema={filterE3TransactionsSchema}
      tableName="e3_transactions"
      fields={fields}
    />
  );
}
